
import { PropTypes } from 'react';
import { locationShape, routerShape } from 'react-router';

/**
 * Errors
 */
export const apiErrorPropTypeShape = {
  message: PropTypes.string.isRequired,
  status: PropTypes.number,
};

export const apiErrorPropType = PropTypes.shape(apiErrorPropTypeShape);

/**
 * Forms
 */
export const formFieldShape = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
};

export const formFieldPropType = PropTypes.shape(formFieldShape);

// Checkbox
export const checkboxInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  defaultValues: PropTypes.arrayOf(PropTypes.string.isRequired),
};

export const checkboxInputFormFieldPropType = PropTypes.shape(checkboxInputFormFieldShape);

// Date
export const dateInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  max: PropTypes.string.isRequired,
  min: PropTypes.string.isRequired,
};

export const dateInputFormFieldPropType = PropTypes.shape(dateInputFormFieldShape);

// Email
export const emailInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
};

export const emailInputFormFieldPropType = PropTypes.shape(emailInputFormFieldShape);

// Group select
export const groupSelectInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  groupValues: PropTypes.arrayOf(PropTypes.shape({ // alias name for values
    key: PropTypes.string.isRequired,
    values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  })).isRequired,
  groupDefaultValues: PropTypes.arrayOf(PropTypes.shape({ // alias name for defaultValues
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired),
};

export const groupSelectInputFormFieldPropType = PropTypes.shape(groupSelectInputFormFieldShape);

// Heading
export const headingFormFieldShape = {
  ...formFieldShape,
};

export const headingFormFieldPropType = PropTypes.shape(headingFormFieldShape);

// Radio
export const radioInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  defaultValue: PropTypes.string,
};

export const radioInputFormFieldPropType = PropTypes.shape(radioInputFormFieldShape);

// Select
export const selectInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  defaultValue: PropTypes.string,
};

export const selectInputFormFieldPropType = PropTypes.shape(selectInputFormFieldShape);

// Textarea
export const textAreaInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
};

export const textAreaInputFormFieldPropType = PropTypes.shape(textAreaInputFormFieldShape);

// Text
export const textInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
};

export const textInputFormFieldPropType = PropTypes.shape(textInputFormFieldShape);

// Address
export const addressInputFormFieldShape = {
  ...formFieldShape,
  exposure: PropTypes.string.isRequired,
  subfields: PropTypes.shape({
    CITY: textInputFormFieldPropType,
    COUNTRY: textInputFormFieldPropType,
    LINE_1: textInputFormFieldPropType,
    LINE_2: textInputFormFieldPropType,
    POSTCODE: textInputFormFieldPropType,
    STATE: textInputFormFieldPropType,
  }),
};

export const addressInputFormFieldPropType = PropTypes.shape(addressInputFormFieldShape);

// Form
export const formShape = {
  fields: PropTypes.arrayOf(
    PropTypes.oneOfType([
      formFieldPropType.isRequired,
      PropTypes.shape(),
    ]).isRequired,
  ).isRequired,
  ticket: PropTypes.shape({
    id: PropTypes.string.isRequired,
    bookingEnabled: PropTypes.bool.isRequired,
    thirdPartyUrl: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    event: PropTypes.shape({
      title: PropTypes.string.isRequired,
      images: PropTypes.shape({
        hero: PropTypes.shape({
          large: PropTypes.string.isRequired,
        }).isRequired,
      }).isRequired,
      location: PropTypes.shape({
        addressSummary: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      }).isRequired,
      organiser: PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        termsAndConditionsUrl: PropTypes.string,
      }),
    }).isRequired,
    price: PropTypes.shape({
      value: PropTypes.number.isRequired,
      originalValue: PropTypes.number.isRequired,
      valueFormatted: PropTypes.string.isRequired,
      originalValueFormatted: PropTypes.string.isRequired,
      currency: PropTypes.shape({
        code: PropTypes.string.isRequired,
        minorUnit: PropTypes.number.isRequired,
      }).isRequired,
      fee: PropTypes.number.isRequired,
      feeFormatted: PropTypes.string.isRequired,
      total: PropTypes.number.isRequired,
      totalFormatted: PropTypes.string.isRequired,
    }).isRequired,
    race: PropTypes.shape({
      title: PropTypes.string.isRequired,
      startDate: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export const formPropType = PropTypes.shape(formShape);

/**
 * Tables
 */
export const tableHeadersShape = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
);

export const tableRowsShape = PropTypes.arrayOf(
  PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
);

/**
 * Images
 */
const imageSizesPropTypeShape = {
  xxlarge: PropTypes.string,
  xlarge: PropTypes.string,
  medium: PropTypes.string,
  large: PropTypes.string,
  thumbnail: PropTypes.string.isRequired,
  original: PropTypes.string,
  facebook: PropTypes.string.isRequired,
  twitter: PropTypes.string.isRequired,
  title: PropTypes.string,
};

const imageSizesPropType = PropTypes.shape(imageSizesPropTypeShape);

/**
 * Organisers
 */
export const organiserAbridgedPropTypeShape = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  description: PropTypes.string,
  excerpt: PropTypes.string,
  images: PropTypes.shape({
    logo: imageSizesPropType,
  }),
  schemaType: PropTypes.string.isRequired,
};

export const organiserAbridgedPropType = PropTypes.shape(organiserAbridgedPropTypeShape);

export const organiserFullPropTypeShape = {
  ...organiserAbridgedPropTypeShape,
  permalink: PropTypes.string.isRequired,
  canonical: PropTypes.string.isRequired,
  social: PropTypes.shape({
    facebook: PropTypes.string,
    twitter: PropTypes.string,
    instagram: PropTypes.string,
  }),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
};

export const organiserFullPropType = PropTypes.shape(organiserFullPropTypeShape);

/**
 * Tickets
 */
export const ticketPropTypeShape = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  availability: PropTypes.shape({
    type: PropTypes.number.isRequired,
    value: PropTypes.string.isRequired,
    from: PropTypes.string,
    fromFormatted: PropTypes.string,
    to: PropTypes.string,
    toFormatted: PropTypes.string,
  }).isRequired,
  currencyLabel: PropTypes.string.isRequired,
  rrp: PropTypes.number.isRequired,
  rrpFormatted: PropTypes.string.isRequired,
  discountPrice: PropTypes.number,
  discountPriceFormatted: PropTypes.string,
  discountCode: PropTypes.string,
  bookingUrl: PropTypes.string.isRequired,
  bookingEnabled: PropTypes.bool.isRequired,
};

export const ticketPropType = PropTypes.shape(ticketPropTypeShape);

/**
 * Races
 */
export const raceAbridgedPropTypeShape = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  excerpt: PropTypes.string,
  canonical: PropTypes.string.isRequired,
  startDate: PropTypes.string,
  startDateFormatted: PropTypes.string,
  featured: PropTypes.bool,
  tickets: PropTypes.shape({
    pricesFrom: PropTypes.number.isRequired,
    pricesTo: PropTypes.number,
    currencyLabel: PropTypes.string.isRequired,
    tickets: PropTypes.arrayOf(ticketPropType),
    discounts: PropTypes.bool,
    maxDiscount: PropTypes.shape({
      discountValue: PropTypes.string.isRequired,
    }),
  }),
  keyDetails: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number.isRequired,
      typeDescriptor: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number.isRequired,
      typeDescriptor: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      link: PropTypes.string,
    }).isRequired,
  ),
  information: PropTypes.shape({
    sport: PropTypes.number,
    sportLabel: PropTypes.string,
    distance: PropTypes.number,
    distanceLabel: PropTypes.string,
    metricLength: PropTypes.number,
  }),
  customContent: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ),
  schemaType: PropTypes.string.isRequired,
};

export const raceAbridgedPropType = PropTypes.shape(raceAbridgedPropTypeShape);

/**
 * Events
 */
export const eventAbridgedPropTypeShape = {
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  permalink: PropTypes.string.isRequired,
  canonical: PropTypes.string.isRequired,
  description: PropTypes.string,
  excerpt: PropTypes.string,
  startDate: PropTypes.string,
  startDateFormatted: PropTypes.string,
  eventUrl: PropTypes.string,
  races: PropTypes.shape({
    races: PropTypes.arrayOf(raceAbridgedPropType).isRequired,
    available: PropTypes.bool,
    pricesFrom: PropTypes.number,
    pricesTo: PropTypes.number,
    currencyLabel: PropTypes.string,
    notifications: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.number.isRequired,
        typeDescriptor: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }).isRequired,
    ),
  }),
  images: PropTypes.shape({
    hero: imageSizesPropType,
  }),
  location: PropTypes.shape({
    lat: PropTypes.number,
    long: PropTypes.number,
    address: PropTypes.arrayOf(PropTypes.string.isRequired),
  }),
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number.isRequired,
      typeDescriptor: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
  ),
  reviews: PropTypes.shape({
    totalFormatted: PropTypes.string.isRequired,
    total: PropTypes.number.isRequired,
    ratings: PropTypes.shape({
      overall: PropTypes.number.isRequired,
      course: PropTypes.number.isRequired,
      organisation: PropTypes.number.isRequired,
      atmosphere: PropTypes.number.isRequired,
    }).isRequired,
  }),
  saves: PropTypes.shape({
    total: PropTypes.number.isRequired,
    users: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }).isRequired,
    ),
  }),
  viewing: PropTypes.number,
  views: PropTypes.number,
};

export const eventAbridgedPropType = PropTypes.shape(eventAbridgedPropTypeShape);

export const eventFullPropTypeShape = {
  ...eventAbridgedPropTypeShape,
  organiser: organiserAbridgedPropType,
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.number.isRequired,
      typeDescriptor: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired,
  ),
  contact: PropTypes.shape({
    email: PropTypes.string,
    phone: PropTypes.string,
    website: PropTypes.string,
  }),
  social: PropTypes.shape({
    facebook: PropTypes.string,
    twitter: PropTypes.string,
    instagram: PropTypes.string,
  }),
  schemaType: PropTypes.string.isRequired,
  flagship: PropTypes.bool,
  stub: PropTypes.bool,
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
};

export const eventFullPropType = PropTypes.shape(eventFullPropTypeShape);

/**
 * Reviews
 */
export const reviewAbridgedPropTypeShape = {
  id: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  author: PropTypes.shape({
    displayName: PropTypes.string.isRequired,
    picture: PropTypes.string.isRequired,
  }).isRequired,
  ratings: PropTypes.shape({
    overall: PropTypes.number.isRequired,
    course: PropTypes.number.isRequired,
    organisation: PropTypes.number.isRequired,
    atmosphere: PropTypes.number.isRequired,
  }).isRequired,
  review: PropTypes.string.isRequired,
  race: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  year: PropTypes.number.isRequired,
};

export const reviewAbridgedPropType = PropTypes.shape(reviewAbridgedPropTypeShape);

/**
 * Google Place API-like object (abridged)
 */
export const googlePlaceShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  geometry: PropTypes.shape({
    location: PropTypes.shape({
      lat: PropTypes.func.isRequired,
      lng: PropTypes.func.isRequired,
    }),
    viewport: PropTypes.shape({
      getNorthEast: PropTypes.func.isRequired,
      getSouthWest: PropTypes.func.isRequired,
    }),
  }).isRequired,
});

/**
 * Users
 */
export const userProfileShape = PropTypes.shape({
  displayName: PropTypes.string.isRequired,
  emailAddress: PropTypes.string,
  picture: PropTypes.string.isRequired,
});

/**
 * GraphQL
 */
export const GQLQueryShape = {
  fetchMore: PropTypes.func,
  loading: PropTypes.bool,
  networkStatus: PropTypes.number,
  refetch: PropTypes.func,
  startPolling: PropTypes.func,
  stopPolling: PropTypes.func,
  subscribeToMore: PropTypes.func,
  updateQuery: PropTypes.func,
  variables: PropTypes.shape(),
};

export const GQLQueryPropType = PropTypes.shape(GQLQueryShape);

/**
 * React router
 */
export const routerParamsPropType = PropTypes.objectOf(PropTypes.string);

export { locationShape as routerLocationPropType };

export { routerShape as routerPropType };

export const routerContextShape = {
  router: routerShape,
  location: PropTypes.object,
  route: PropTypes.object,
  routes: PropTypes.array,
  params: PropTypes.object,
  components: PropTypes.array,
  createElement: PropTypes.func,
};



// WEBPACK FOOTER //
// ./app/src/proptypes.js